/**
 * WorkspaceDetailPage - RAG Workspace Detail View
 *
 * Mirrors RAGFlow's dataset detail page with document table,
 * upload, and parsing controls.
 *
 * Migrated to Eliza Design System (Feb 2026)
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  LinkIcon,
  BookOpenIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  PlusIcon,
  PlayIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { Layout } from '../../components/layout/Layout';
import {
  Button,
  Badge,
  Spinner,
  Input,
  Select,
  SelectOption,
  Tabs,
  TabsContent,
} from '../../components/ui';
import { useToasts } from '../../stores/useToasts';
import { useAuth } from '../../contexts/AuthContext';
import {
  WorkspaceConnectorAccessPanel,
  getAgentMeshPlannerMode,
  type PlannerMode,
} from '../../components/domains/WorkspaceConnectorAccessPanel';
import {
  deriveStructuredAnalyticsDraft,
  type SemanticShellSurface,
  WorkspaceSemanticContextShell,
} from '../../components/workspace-text-to-sql';
import {
  workspaceSupportsChatWithoutKnowledgeBase,
} from '../../lib/workspace-chat';
import { getWorkspaceCapabilities } from '../../lib/workspace-capabilities';
import {
  mapSemanticContextToStructuredDraft,
  useApplyWorkspaceOntologySuggestion,
  useApplyWorkspaceOntologySuggestionsBatch,
  useActivateWorkspaceSemanticRuntimePack,
  useCompileWorkspaceSemanticRuntimePack,
  useCreateWorkspaceOntologyGenerationRun,
  useCreateWorkspaceVerifiedQuery,
  useDeleteWorkspaceVerifiedQuery,
  useDraftWorkspaceVerifiedQueryWithAI,
  useRejectWorkspaceOntologySuggestion,
  useReviewWorkspaceVerifiedQuery,
  useSaveWorkspaceSemanticOntologyDraft,
  useUpdateWorkspaceVerifiedQuery,
  useUpdateWorkspaceSemanticStructuredScope,
  useWorkspaceOntologyGenerationSettings,
  useWorkspaceOntologyGenerationRunStream,
  useWorkspaceOntologyGenerationSuggestions,
  useWorkspaceSemanticContext,
  useWorkspaceSemanticMCPPolicy,
  useWorkspaceSemanticMCPReadiness,
  useWorkspaceVerifiedQueries,
} from '../../hooks/useWorkspaceSemanticContext';

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');

interface RAGFlowDocument {
  id: number;
  original_filename: string;
  file_size: number;
  mime_type: string;
  status: 'pending' | 'parsing' | 'completed' | 'failed';
  progress: number;
  chunk_count: number;
  token_count: number;
  processing_error?: string;
  created_at: string;
}

interface KnowledgeBaseSummary {
  id: number;
  name: string;
  status: string;
  document_count: number;
  chunk_count: number;
  is_active: boolean;
  description?: string;
  parser_type?: string;
  source_type?: string;
}

interface RAGFlowWorkspace {
  mcp_enabled: boolean;
  mcp_admin_allowed: boolean;
  id: number;
  name: string;
  display_name: string;
  description?: string;
  updated_at?: string;
  icon: string;
  color: string;
  status: string;
  document_count: number;
  chunk_count: number;
  total_tokens: number;
  template_name?: string;
  template_display_name?: string;
  knowledge_base_count?: number;
  knowledge_bases?: KnowledgeBaseSummary[];
  workspace_config?: Record<string, unknown> | null;
}

interface WorkspaceDetailPayload {
  workspace: RAGFlowWorkspace;
  knowledge_bases: KnowledgeBaseSummary[];
}

function normalizeWorkspaceConfig(
  workspaceConfig: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  return workspaceConfig && typeof workspaceConfig === 'object' ? { ...workspaceConfig } : {};
}

function normalizeChatConfig(
  workspaceConfig: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const normalizedConfig = normalizeWorkspaceConfig(workspaceConfig);
  const chat = normalizedConfig.chat;
  return chat && typeof chat === 'object' ? { ...(chat as Record<string, unknown>) } : {};
}

type WorkspaceContentTab = 'overview' | 'knowledge' | 'semantic' | 'connectors' | 'members' | 'settings';

const SEMANTIC_SURFACE_BY_SLUG: Record<string, SemanticShellSurface> = {
  'data-sources': 'overview',
  setup: 'setup',
  ontology: 'ontology',
  'verified-queries': 'verified_queries',
  'context-packs': 'context_packs',
  corrections: 'corrections',
  mcp: 'mcp',
};

const SEMANTIC_SLUG_BY_SURFACE: Record<Exclude<SemanticShellSurface, 'overview'>, string> = {
  setup: 'setup',
  ontology: 'ontology',
  verified_queries: 'verified-queries',
  context_packs: 'context-packs',
  corrections: 'corrections',
  mcp: 'mcp',
};

const SEMANTIC_SURFACE_LABEL: Record<SemanticShellSurface, string> = {
  overview: 'Overview',
  setup: 'Setup Guide',
  ontology: 'Semantic Ontology',
  verified_queries: 'Verified Queries',
  context_packs: 'Context Packs',
  corrections: 'Corrections Raised',
  mcp: 'MCP & External Access',
};

export function resolveWorkspaceContentTab(
  workspaceSection?: string,
  semanticSurface?: string,
): WorkspaceContentTab {
  if (semanticSurface) return 'semantic';
  if (workspaceSection === 'semantic') return 'semantic';
  if (workspaceSection === 'knowledge') return 'knowledge';
  if (workspaceSection === 'connectors') return 'connectors';
  if (workspaceSection === 'members') return 'members';
  if (workspaceSection === 'settings') return 'settings';
  return 'overview';
}

function resolveSemanticSurface(semanticSurface?: string): SemanticShellSurface {
  if (!semanticSurface) return 'overview';
  return SEMANTIC_SURFACE_BY_SLUG[semanticSurface] ?? 'overview';
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// Status badge component using DS Badge
function StatusBadge({ status, progress }: { status: string; progress?: number }) {
  const pct = progress || 0;

  switch (status) {
    case 'completed':
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircleIcon className="w-3 h-3" />
          Success
        </Badge>
      );
    case 'parsing':
      return (
        <div className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          <ArrowPathIcon className="w-3 h-3 animate-spin" />
          <div className="w-16 h-1.5 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-500 ease-out"
              style={{ width: `${Math.max(pct, 5)}%` }}
            />
          </div>
          <span className="min-w-[2rem] text-right">{pct}%</span>
        </div>
      );
    case 'failed':
      return (
        <Badge variant="danger" className="gap-1">
          <ExclamationCircleIcon className="w-3 h-3" />
          Failed
        </Badge>
      );
    case 'pending':
    default:
      return (
        <Badge variant="warning" className="gap-1">
          <PlayIcon className="w-3 h-3" />
          Queued
        </Badge>
      );
  }
}

function knowledgeBaseStatusVariant(status?: string): 'success' | 'warning' | 'danger' | 'secondary' {
  const normalized = String(status || '').toLowerCase();
  if (['active', 'ready', 'completed', 'synced'].includes(normalized)) return 'success';
  if (['failed', 'error'].includes(normalized)) return 'danger';
  if (['pending', 'indexing', 'syncing'].includes(normalized)) return 'warning';
  return 'secondary';
}

function sourceTypeLabel(sourceType?: string) {
  if (!sourceType) return 'Workspace upload';
  if (sourceType === 'elasticsearch') return 'Platform upload';
  if (sourceType === 's3') return 'S3 sync';
  return sourceType
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function SummaryStat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
        {label}
      </p>
      <p className="mt-2 truncate text-lg font-semibold text-charcoal dark:text-gray-100">
        {value}
        {detail && (
          <span className="ml-1 text-sm font-normal text-gray-500 dark:text-gray-400">{detail}</span>
        )}
      </p>
    </div>
  );
}

export default function WorkspaceDetailPage() {
  const { workspaceId, workspaceSection, semanticSurface } = useParams<{
    workspaceId: string;
    workspaceSection?: string;
    semanticSurface?: string;
  }>();
  const navigate = useNavigate();
  const token = localStorage.getItem('auth_token');
  const { push: addToast } = useToasts();
  const { hasAnyPermission } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const agentCoreFileInputRef = useRef<HTMLInputElement>(null);

  const [workspace, setWorkspace] = useState<RAGFlowWorkspace | null>(null);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseSummary[]>([]);
  const [selectedKnowledgeBaseId, setSelectedKnowledgeBaseId] = useState<number | null>(null);
  const selectedKnowledgeBaseIdRef = useRef<number | null>(null);
  const selectKnowledgeBaseId = useCallback((nextKnowledgeBaseId: number | null) => {
    selectedKnowledgeBaseIdRef.current = nextKnowledgeBaseId;
    setSelectedKnowledgeBaseId(nextKnowledgeBaseId);
  }, []);
  const [documents, setDocuments] = useState<RAGFlowDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSyncingStats, setIsSyncingStats] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [deletingKnowledgeBaseId, setDeletingKnowledgeBaseId] = useState<number | null>(null);
  const [showCreateKbModal, setShowCreateKbModal] = useState(false);
  const [isCreatingKb, setIsCreatingKb] = useState(false);
  const [kbName, setKbName] = useState('');
  const [kbDescription, setKbDescription] = useState('');
  const [kbSourceType, setKbSourceType] = useState<'elasticsearch' | 's3' | 'local_directory'>('elasticsearch');
  const [kbSourceValue, setKbSourceValue] = useState('');
  const [kbParserType, setKbParserType] = useState<'naive' | 'deepdoc' | 'gpt-4o' | 'docling' | 'custom-vlm'>('naive');
  const [kbCustomModel, setKbCustomModel] = useState('deepseek-ocr2@OpenAI-API-Compatible');

  // AgentCore document upload state
  const [acUploading, setAcUploading] = useState(false);
  const [acSyncing, setAcSyncing] = useState(false);
  const [acUploadedFiles, setAcUploadedFiles] = useState<string[]>([]);

  // AgentCore config state
  const [acConfig, setAcConfig] = useState<{
    configured: boolean;
    runtime_arn: string;
    runtime_region: string;
    endpoint_qualifier: string;
    system_prompt: string;
    model_id: string;
  } | null>(null);
  const [acSaving, setAcSaving] = useState(false);
  const [acRuntimeArn, setAcRuntimeArn] = useState('');
  const [acRegion, setAcRegion] = useState('us-east-1');
  const [acQualifier, setAcQualifier] = useState('DEFAULT');
  const [acSystemPrompt, setAcSystemPrompt] = useState('');
  const [acModelId, setAcModelId] = useState('');

  // RAG & AI settings (embedding + inference model)
  const [ragSettings, setRagSettings] = useState<{
    embedding_provider: string;
    embedding_model: string;
    available_embedding_providers: string[];
    default_llm_model?: string | null;
  } | null>(null);
  const [promptTemplate, setPromptTemplate] = useState<{ model: string } | null>(null);
  const [ragChatModels, setRagChatModels] = useState<Array<{ id: string; name: string; provider: string }>>([]);
  const [savingChatModel, setSavingChatModel] = useState(false);
  const [savingPlannerMode, setSavingPlannerMode] = useState<PlannerMode | null>(null);
  const activeTab = resolveWorkspaceContentTab(workspaceSection, semanticSurface);
  const activeSemanticSurface = resolveSemanticSurface(semanticSurface);
  const workspaceCapabilities = getWorkspaceCapabilities(workspace);
  const isAgentMeshWorkspace = workspaceCapabilities.supportsAgentMeshPlanner;
  const isSemanticOntologySurface =
    activeTab === 'semantic' &&
    activeSemanticSurface === 'ontology' &&
    workspaceCapabilities.supportsOntologyAuthoring;
  const workspaceDisplayName = workspace?.display_name || workspace?.name || 'Workspace';
  const workspaceBreadcrumbs = useMemo(() => {
    const currentLabel =
      activeTab === 'semantic'
        ? SEMANTIC_SURFACE_LABEL[activeSemanticSurface]
        : activeTab === 'knowledge'
          ? workspaceCapabilities.supportsAgentCoreDocumentUpload
            ? 'AgentCore Knowledge'
            : 'Knowledge Base'
          : activeTab === 'connectors'
            ? 'Connectors'
            : activeTab === 'members'
              ? 'Members'
              : activeTab === 'settings'
                ? 'Workspace Settings'
                : 'Overview';

    return [
      { label: 'Workspaces', path: '/workspaces' },
      { label: workspaceDisplayName, path: workspaceId ? `/workspaces/${workspaceId}` : undefined },
      { label: currentLabel },
    ];
  }, [activeSemanticSurface, activeTab, workspaceCapabilities.supportsAgentCoreDocumentUpload, workspaceDisplayName, workspaceId]);
  const setActiveTab = useCallback((nextTab: string) => {
    if (!workspaceId) return;
    if (nextTab === 'semantic') {
      navigate(`/workspaces/${workspaceId}/semantic/ontology`);
      return;
    }
    if (nextTab === 'knowledge') {
      navigate(`/workspaces/${workspaceId}/knowledge`);
      return;
    }
    if (nextTab === 'connectors') {
      navigate(`/workspaces/${workspaceId}/connectors`);
      return;
    }
    if (nextTab === 'members' || nextTab === 'settings') {
      navigate(`/workspaces/${workspaceId}/${nextTab}`);
      return;
    }
    navigate(`/workspaces/${workspaceId}`);
  }, [navigate, workspaceId]);
  const setActiveSemanticSurface = useCallback((surface: SemanticShellSurface) => {
    if (!workspaceId) return;
    if (surface === 'overview') {
      navigate(`/workspaces/${workspaceId}`);
      return;
    }
    navigate(`/workspaces/${workspaceId}/semantic/${SEMANTIC_SLUG_BY_SURFACE[surface]}`);
  }, [navigate, workspaceId]);
  const numericWorkspaceId = workspaceId ? Number(workspaceId) : null;
  const [searchParams] = useSearchParams();
  const persistedOntologyGenerationRunId = useMemo(() => {
    const rawRunId = searchParams.get('generation_run_id');
    if (!rawRunId) return null;
    const parsedRunId = Number.parseInt(rawRunId, 10);
    return Number.isSafeInteger(parsedRunId) && parsedRunId > 0 ? parsedRunId : null;
  }, [searchParams]);
  const [activeOntologyGenerationRunId, setActiveOntologyGenerationRunId] =
    useState<number | null>(persistedOntologyGenerationRunId);
  const shouldLoadMcpSemanticData =
    activeTab === 'semantic' &&
    activeSemanticSurface === 'mcp' &&
    workspaceCapabilities.supportsMcpExposure;
  const shouldLoadOntologyGenerationData =
    activeTab === 'semantic' &&
    activeSemanticSurface === 'ontology' &&
    workspaceCapabilities.supportsOntologyAuthoring;
  const shouldLoadVerifiedQueriesData =
    activeTab === 'semantic' &&
    activeSemanticSurface === 'verified_queries' &&
    workspaceCapabilities.supportsVerifiedQueries;
  const shouldLoadSemanticContext =
    workspaceCapabilities.supportsStructuredWarehouseScope ||
    workspaceCapabilities.supportsOntologyAuthoring ||
    workspaceCapabilities.supportsContextPacks ||
    workspaceCapabilities.supportsVerifiedQueries ||
    workspaceCapabilities.supportsMcpExposure;
  const semanticContextQuery = useWorkspaceSemanticContext(numericWorkspaceId, shouldLoadSemanticContext);
  const mcpPolicyQuery = useWorkspaceSemanticMCPPolicy(numericWorkspaceId, shouldLoadMcpSemanticData);
  const mcpReadinessQuery = useWorkspaceSemanticMCPReadiness(numericWorkspaceId, shouldLoadMcpSemanticData);
  const updateSemanticScope = useUpdateWorkspaceSemanticStructuredScope(numericWorkspaceId);
  const saveSemanticOntologyDraft = useSaveWorkspaceSemanticOntologyDraft(numericWorkspaceId);
  const compileSemanticRuntimePack = useCompileWorkspaceSemanticRuntimePack(numericWorkspaceId);
  const publishSemanticRuntimePack = useActivateWorkspaceSemanticRuntimePack(numericWorkspaceId);
  const ontologyGenerationSettingsQuery = useWorkspaceOntologyGenerationSettings(
    numericWorkspaceId,
    shouldLoadOntologyGenerationData,
  );
  const ontologyGenerationSuggestionsQuery = useWorkspaceOntologyGenerationSuggestions(
    numericWorkspaceId,
    shouldLoadOntologyGenerationData,
  );
  const createOntologyGenerationRun = useCreateWorkspaceOntologyGenerationRun(numericWorkspaceId);
  const ontologyGenerationRunStream = useWorkspaceOntologyGenerationRunStream(
    shouldLoadOntologyGenerationData ? numericWorkspaceId : null,
    activeOntologyGenerationRunId,
    persistedOntologyGenerationRunId != null,
  );
  const applyOntologySuggestion = useApplyWorkspaceOntologySuggestion(numericWorkspaceId);
  const applyOntologySuggestionsBatch = useApplyWorkspaceOntologySuggestionsBatch(
    numericWorkspaceId,
  );
  const rejectOntologySuggestion = useRejectWorkspaceOntologySuggestion(numericWorkspaceId);
  const verifiedQueriesQuery = useWorkspaceVerifiedQueries(
    numericWorkspaceId,
    shouldLoadVerifiedQueriesData,
  );
  const createVerifiedQuery = useCreateWorkspaceVerifiedQuery(numericWorkspaceId);
  const draftVerifiedQueryWithAI = useDraftWorkspaceVerifiedQueryWithAI(numericWorkspaceId);
  const updateVerifiedQuery = useUpdateWorkspaceVerifiedQuery(numericWorkspaceId);
  const reviewVerifiedQuery = useReviewWorkspaceVerifiedQuery(numericWorkspaceId);
  const deleteVerifiedQuery = useDeleteWorkspaceVerifiedQuery(numericWorkspaceId);

  useEffect(() => {
    setActiveOntologyGenerationRunId(persistedOntologyGenerationRunId);
  }, [numericWorkspaceId, persistedOntologyGenerationRunId]);

  const fetchWorkspaceWithFallback = useCallback(async (): Promise<WorkspaceDetailPayload | null> => {
    if (!workspaceId) {
      return null;
    }

    const res = await fetch(`${API_BASE}/v1/workspaces/${workspaceId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.ok) {
      const data = await res.json();
      const workspace = data.workspace || data;
      const knowledgeBases: KnowledgeBaseSummary[] = data.knowledge_bases || workspace.knowledge_bases || [];
      return { workspace, knowledge_bases: knowledgeBases };
    }

    const legacyRes = await fetch(`${API_BASE}/v1/ragflow/domains/${workspaceId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!legacyRes.ok) {
      return null;
    }

    const legacyData = await legacyRes.json();
    const workspace = legacyData.workspace || legacyData;
    const knowledgeBases: KnowledgeBaseSummary[] = legacyData.knowledge_bases || workspace.knowledge_bases || [];
    return { workspace, knowledge_bases: knowledgeBases };
  }, [workspaceId, token]);

  const syncWorkspaceStats = useCallback(
    async (silent = false): Promise<boolean> => {
      if (!workspaceId) return false;
      if (!silent) setIsSyncingStats(true);

      try {
        const res = await fetch(`${API_BASE}/v1/ragflow/domains/${workspaceId}/sync-stats`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok && !silent) {
          const err = await res.json().catch(() => ({}));
          addToast({ kind: 'error', message: err.detail || 'Failed to sync stats' });
        }
        return res.ok;
      } catch (e) {
        if (!silent) {
          addToast({ kind: 'error', message: 'Failed to sync stats' });
        }
        return false;
      } finally {
        if (!silent) setIsSyncingStats(false);
      }
    },
    [workspaceId, token, addToast]
  );

  // Fetch workspace and documents
  const fetchData = useCallback(async (requestedKnowledgeBaseId?: number | null) => {
    if (!workspaceId) return;

    try {
      const loadedWorkspace = await fetchWorkspaceWithFallback();
      if (!loadedWorkspace) {
        addToast({ kind: 'error', message: 'Workspace not found' });
        navigate('/workspaces');
        return;
      }

      const nextWorkspace = loadedWorkspace.workspace;
      const nextKnowledgeBases = loadedWorkspace.knowledge_bases || [];
      setWorkspace(nextWorkspace);
      setKnowledgeBases(nextKnowledgeBases);
      const nextWorkspaceCapabilities = getWorkspaceCapabilities(nextWorkspace);

      const preferredKnowledgeBaseId =
        requestedKnowledgeBaseId !== undefined
          ? requestedKnowledgeBaseId
          : selectedKnowledgeBaseIdRef.current;
      const nextSelectedKbId = preferredKnowledgeBaseId && nextKnowledgeBases.some((kb) => kb.id === preferredKnowledgeBaseId)
        ? preferredKnowledgeBaseId
        : (nextKnowledgeBases[0]?.id ?? null);
      selectKnowledgeBaseId(nextSelectedKbId);

      const shouldLoadKnowledgeDocuments =
        activeTab === 'knowledge' || requestedKnowledgeBaseId !== undefined;
      if (shouldLoadKnowledgeDocuments) {
        const docsUrl = new URL(`${API_BASE}/v1/ragflow/domains/${workspaceId}/documents`, window.location.origin);
        if (nextSelectedKbId) {
          docsUrl.searchParams.set('knowledge_base_id', String(nextSelectedKbId));
        }
        const docsRes = await fetch(docsUrl.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (docsRes.ok) {
          const data = await docsRes.json();
          setDocuments(data.documents || []);
        }
      }

      if (activeTab === 'settings' && nextWorkspaceCapabilities.supportsRagPromptTemplateSettings) {
        // RAG & AI settings (embedding + inference model options)
        const [ragRes, templateRes, forRagRes] = await Promise.all([
          fetch(`${API_BASE}/v1/config/rag-settings`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE}/v1/workspaces/${workspaceId}/prompt-template`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE}/v1/models/for-rag`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (ragRes.ok) {
          const data = await ragRes.json();
          setRagSettings(data);
        }
        if (templateRes.ok) {
          const data = await templateRes.json();
          setPromptTemplate({ model: data.model });
        }
        if (forRagRes.ok) {
          const data = await forRagRes.json();
          setRagChatModels(data.models || []);
        }
      }

      if (activeTab === 'settings' && nextWorkspaceCapabilities.supportsAgentCoreRuntimeSettings) {
        // AgentCore config (if this is an agentcore workspace)
        try {
          const acRes = await fetch(`${API_BASE}/v1/workspaces/${workspaceId}/agentcore-config`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (acRes.ok) {
            const data = await acRes.json();
            setAcConfig(data);
            if (data.configured) {
              setAcRuntimeArn(data.runtime_arn || '');
              setAcRegion(data.runtime_region || 'us-east-1');
              setAcQualifier(data.endpoint_qualifier || 'DEFAULT');
              setAcSystemPrompt(data.system_prompt || '');
              setAcModelId(data.model_id || '');
            }
          }
        } catch { }
      }

    } catch (e) {
      console.error('Failed to fetch data:', e);
      addToast({ kind: 'error', message: 'Failed to load workspace' });
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, workspaceId, token, navigate, addToast, fetchWorkspaceWithFallback, selectKnowledgeBaseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectedKnowledgeBase =
    selectedKnowledgeBaseId != null
      ? (knowledgeBases.find((kb) => kb.id === selectedKnowledgeBaseId) ?? null)
      : null;
  const selectedKnowledgeBaseSyncing =
    selectedKnowledgeBase != null
      ? ['pending', 'indexing'].includes(String(selectedKnowledgeBase.status || '').toLowerCase())
      : false;

  // Poll for updates while docs parse OR KB sync/indexing is active.
  useEffect(() => {
    const hasActiveDocProcessing = documents.some((d) => d.status === 'parsing' || d.status === 'pending');
    if (!hasActiveDocProcessing && !selectedKnowledgeBaseSyncing) return;

    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [documents, fetchData, selectedKnowledgeBaseSyncing]);

  const supportsChatWithoutKnowledgeBase = workspaceSupportsChatWithoutKnowledgeBase(workspace);
  const canManageConnections = hasAnyPermission(['connections:read', 'platform:admin']);

  const handleSaveWorkspaceConfig = useCallback(
    async (nextWorkspaceConfig: Record<string, unknown>) => {
      if (!workspaceId || !workspace) {
        return;
      }

      const res = await fetch(`${API_BASE}/v1/workspaces/${workspaceId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workspace_config: nextWorkspaceConfig }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update workspace source access');
      }

      const updated = await res.json();
      setWorkspace((prev) =>
        prev
          ? {
            ...prev,
            workspace_config: updated.workspace_config ?? null,
            updated_at: updated.updated_at ?? prev.updated_at,
          }
          : prev,
      );
      addToast({ kind: 'success', message: 'Workspace source access updated' });
    },
    [workspaceId, workspace, token, addToast],
  );

  const handlePlannerModeChange = useCallback(
    async (nextMode: PlannerMode) => {
      const nextWorkspaceConfig = normalizeWorkspaceConfig(workspace?.workspace_config);
      const nextChatConfig = normalizeChatConfig(workspace?.workspace_config);
      nextChatConfig.planner_mode = nextMode;
      nextWorkspaceConfig.chat = nextChatConfig;

      setSavingPlannerMode(nextMode);
      try {
        await handleSaveWorkspaceConfig(nextWorkspaceConfig);
      } finally {
        setSavingPlannerMode(null);
      }
    },
    [handleSaveWorkspaceConfig, workspace?.workspace_config],
  );

  const [isUpdatingMcp, setIsUpdatingMcp] = useState(false);

  const handleToggleMcp = useCallback(
    async (enabled: boolean) => {
      if (!workspaceId || !workspace) return;

      setIsUpdatingMcp(true);
      try {
        const res = await fetch(`${API_BASE}/v1/workspaces/${workspaceId}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mcp_enabled: enabled }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to update MCP setting');
        }

        const updated = await res.json();
        setWorkspace((prev) =>
          prev
            ? {
              ...prev,
              mcp_enabled: updated.mcp_enabled,
              updated_at: updated.updated_at ?? prev.updated_at,
            }
            : prev,
        );
        addToast({
          kind: 'success',
          message: enabled ? 'MCP integration enabled' : 'MCP integration disabled',
        });
        void mcpPolicyQuery.refetch();
        void mcpReadinessQuery.refetch();
      } catch (err) {
        addToast({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to update MCP setting',
        });
      } finally {
        setIsUpdatingMcp(false);
      }
    },
    [workspaceId, workspace, token, addToast, mcpPolicyQuery, mcpReadinessQuery],
  );

  const handleOpenConnectionManager = useCallback(
    (sourceType?: string, options?: { create?: boolean }) => {
      const params = new URLSearchParams();
      if (sourceType) {
        params.set('type', sourceType);
      }
      if (workspaceId) {
        params.set('workspaceId', workspaceId);
      }
      if (workspace?.display_name || workspace?.name) {
        params.set('workspaceName', workspace?.display_name || workspace?.name || 'Workspace');
      }
      if (options?.create) {
        params.set('new', '1');
      }
      if (isAgentMeshWorkspace) {
        params.set('focus', 'agent-mesh');
      }
      navigate(`/data-connections${params.toString() ? `?${params.toString()}` : ''}`);
    },
    [isAgentMeshWorkspace, navigate, workspace?.display_name, workspace?.name, workspaceId],
  );

  const navigateDataConnectionsForStructured = useCallback(
    (opts: { category: 'warehouse' | 'catalog'; sourceType?: string }) => {
      const params = new URLSearchParams();
      params.set('category', opts.category);
      if (opts.sourceType) {
        params.set('type', opts.sourceType);
      }
      if (workspaceId) {
        params.set('workspaceId', workspaceId);
      }
      if (workspace?.display_name || workspace?.name) {
        params.set('workspaceName', workspace?.display_name || workspace?.name || 'Workspace');
      }
      if (isAgentMeshWorkspace) {
        params.set('focus', 'agent-mesh');
      }
      navigate(`/data-connections?${params.toString()}`);
    },
    [isAgentMeshWorkspace, navigate, workspace?.display_name, workspace?.name, workspaceId],
  );

  const structuredAnalyticsDraft = useMemo(
    () =>
      semanticContextQuery.data
        ? mapSemanticContextToStructuredDraft(semanticContextQuery.data)
        : deriveStructuredAnalyticsDraft(workspace?.workspace_config),
    [semanticContextQuery.data, workspace?.workspace_config],
  );

  const handleChatModelChange = async (modelId: string) => {
    if (!workspaceId || !token) return;
    setSavingChatModel(true);
    try {
      const putRes = await fetch(`${API_BASE}/v1/workspaces/${workspaceId}/prompt-template`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ model: modelId }),
      });
      if (!putRes.ok) throw new Error('Failed to save');
      const updated = await putRes.json();
      setPromptTemplate({ model: updated.model });
      addToast({ kind: 'success', message: 'Chat model updated' });
    } catch (e) {
      addToast({ kind: 'error', message: e instanceof Error ? e.message : 'Failed to update chat model' });
    } finally {
      setSavingChatModel(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (files: FileList) => {
    if (!files.length) return;
    if (!selectedKnowledgeBaseId) {
      addToast({
        kind: 'warning',
        message: 'Create and select a knowledge base before uploading files.',
      });
      return;
    }

    setIsUploading(true);
    let successCount = 0;
    let failCount = 0;
    const failureReasons: string[] = [];

    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('knowledge_base_id', String(selectedKnowledgeBaseId));

        const res = await fetch(`${API_BASE}/v1/ragflow/domains/${workspaceId}/documents`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (res.ok) {
          successCount++;
        } else {
          failCount++;
          const error = await res.json().catch(() => ({}));
          const detail = typeof error?.detail === 'string' ? error.detail : `Upload failed (${res.status})`;
          failureReasons.push(`${file.name}: ${detail}`);
          console.error(`Upload failed for ${file.name}:`, error);
        }
      } catch (e) {
        failCount++;
        failureReasons.push(`${file.name}: ${e instanceof Error ? e.message : 'Unexpected upload error'}`);
        console.error(`Upload error for ${file.name}:`, e);
      }
    }

    setIsUploading(false);

    if (successCount > 0) {
      addToast({ kind: 'success', message: `Uploaded ${successCount} file(s)` });
    }
    if (failCount > 0) {
      addToast({
        kind: 'error',
        message:
          failureReasons.length > 0
            ? `${failCount} file(s) failed. ${failureReasons[0]}`
            : `${failCount} file(s) failed to upload`,
      });
    }

    fetchData();
  };

  const handleSaveAgentCoreSettings = async () => {
    if (!workspaceId || !token) return;
    setAcSaving(true);
    try {
      const res = await fetch(`${API_BASE}/v1/workspaces/${workspaceId}/agentcore-config`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runtime_arn: acRuntimeArn || acConfig?.runtime_arn || '',
          runtime_region: acRegion || acConfig?.runtime_region || 'us-east-1',
          endpoint_qualifier: acQualifier || acConfig?.endpoint_qualifier || 'DEFAULT',
          system_prompt: acSystemPrompt.trim() || null,
          model_id: acModelId.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save AgentCore settings');
      }
      const data = await res.json();
      setAcConfig(data);
      addToast({ kind: 'success', message: 'AgentCore settings saved' });
    } catch (e) {
      addToast({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Failed to save AgentCore settings',
      });
    } finally {
      setAcSaving(false);
    }
  };

  const handleAgentCoreUpload = async (files: FileList | null) => {
    if (!files?.length || !workspaceId || !token) return;
    setAcUploading(true);
    let successCount = 0;
    let failCount = 0;
    let firstFailure: string | null = null;

    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_BASE}/v1/workspaces/${workspaceId}/agentcore-documents`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || err.message || `Upload failed (${res.status})`);
        }
        successCount++;
        setAcUploadedFiles((prev) => [...prev, file.name]);
      } catch (e) {
        failCount++;
        firstFailure = firstFailure || `${file.name}: ${e instanceof Error ? e.message : 'Upload failed'}`;
      }
    }

    if (successCount > 0) {
      addToast({ kind: 'success', message: `${successCount} file(s) uploaded to AgentCore documents` });
    }
    if (failCount > 0) {
      addToast({ kind: 'error', message: firstFailure || `${failCount} AgentCore upload(s) failed` });
    }
    setAcUploading(false);
  };

  const handleAgentCoreSync = async () => {
    if (!workspaceId || !token) return;
    setAcSyncing(true);
    try {
      const res = await fetch(`${API_BASE}/v1/workspaces/${workspaceId}/agentcore-documents/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'AgentCore knowledge sync failed');
      }
      addToast({
        kind: 'success',
        message: 'AgentCore knowledge sync started. Bedrock indexing may take a short time.',
      });
    } catch (e) {
      addToast({
        kind: 'error',
        message: e instanceof Error ? e.message : 'AgentCore knowledge sync failed',
      });
    } finally {
      setAcSyncing(false);
    }
  };

  const handleCreateKnowledgeBase = async () => {
    const trimmedName = kbName.trim();
    if (!trimmedName) {
      addToast({ kind: 'error', message: 'Knowledge base name is required' });
      return;
    }
    if (kbSourceType === 's3' && !kbSourceValue.trim()) {
      addToast({ kind: 'error', message: 'S3 bucket path is required for S3 knowledge bases' });
      return;
    }

    const sourceConfig: Record<string, unknown> = {};
    if (kbSourceValue.trim()) {
      if (kbSourceType === 'local_directory') {
        sourceConfig.path = kbSourceValue.trim();
      } else if (kbSourceType === 's3') {
        sourceConfig.prefix = kbSourceValue.trim();
      } else if (kbSourceType === 'elasticsearch') {
        sourceConfig.index_name = kbSourceValue.trim();
      }
    }

    const parserConfig: Record<string, unknown> = {};
    if (kbParserType === 'custom-vlm' && kbCustomModel.trim()) {
      parserConfig.custom_vlm_model = kbCustomModel.trim();
    }

    const payload: Record<string, unknown> = {
      name: trimmedName,
      description: kbDescription.trim() || undefined,
      parser_type: kbParserType,
      parser_config: Object.keys(parserConfig).length ? parserConfig : undefined,
      source_type: kbSourceType,
      source_config: Object.keys(sourceConfig).length ? sourceConfig : undefined,
      storage_backend: 'tenant_default',
      chunk_token_count: 512,
      similarity_threshold: 20,
      top_k: 5,
    };

    setIsCreatingKb(true);
    try {
      const res = await fetch(`${API_BASE}/v1/workspaces/${workspaceId}/knowledge-bases`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create knowledge base');
      }
      const createdKb = await res.json();
      const createdSummary: KnowledgeBaseSummary = {
        id: createdKb.id,
        name: createdKb.name,
        status: createdKb.status || 'pending',
        document_count: createdKb.document_count ?? 0,
        chunk_count: createdKb.chunk_count ?? 0,
        is_active: createdKb.is_active ?? true,
        description: createdKb.description,
        parser_type: createdKb.parser_type,
        source_type: createdKb.source_type,
      };
      setKnowledgeBases((prev) => [createdSummary, ...prev.filter((kb) => kb.id !== createdSummary.id)]);
      addToast({
        kind: 'success',
        message:
          kbSourceType === 's3'
            ? 'Knowledge base created. Initial S3 sync started.'
            : 'Knowledge base created',
      });
      setShowCreateKbModal(false);
      setKbName('');
      setKbDescription('');
      setKbSourceType('s3');
      setKbSourceValue('');
      setKbParserType('naive');
      setKbCustomModel('deepseek-ocr2@OpenAI-API-Compatible');
      setDocuments([]);
      selectKnowledgeBaseId(createdKb.id);
      void fetchData(createdKb.id);
    } catch (e) {
      addToast({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Failed to create knowledge base',
      });
    } finally {
      setIsCreatingKb(false);
    }
  };

  // Delete knowledge base
  const handleDeleteKnowledgeBase = async (kb: KnowledgeBaseSummary) => {
    if (!workspaceId || !token || deletingKnowledgeBaseId !== null) return;
    const confirmed = window.confirm(
      `Delete "${kb.name}"?\n\nThis will delete the knowledge base, every file attached to it, and the indexed chunks used for retrieval. This action cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingKnowledgeBaseId(kb.id);
    try {
      const res = await fetch(`${API_BASE}/v1/workspaces/${workspaceId}/knowledge-bases/${kb.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to delete knowledge base');
      }

      setKnowledgeBases((prev) => prev.filter((item) => item.id !== kb.id));
      if (selectedKnowledgeBaseId === kb.id) {
        const nextKb = knowledgeBases.find((item) => item.id !== kb.id) ?? null;
        selectKnowledgeBaseId(nextKb?.id ?? null);
        setDocuments([]);
      }
      addToast({ kind: 'success', message: 'Knowledge base deleted' });
      await fetchData();
    } catch (e) {
      addToast({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Failed to delete knowledge base',
      });
    } finally {
      setDeletingKnowledgeBaseId(null);
    }
  };

  // Delete document
  const handleDeleteDocument = async (docId: number) => {
    const document = documents.find((doc) => doc.id === docId);
    const confirmed = window.confirm(
      `Delete "${document?.original_filename ?? 'this file'}"?\n\nThis will remove the file from this knowledge base and delete its indexed chunks. This action cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      const res = await fetch(`${API_BASE}/v1/ragflow/domains/${workspaceId}/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        addToast({ kind: 'success', message: 'Document deleted' });
        fetchData();
      } else {
        addToast({ kind: 'error', message: 'Failed to delete document' });
      }
    } catch (e) {
      addToast({ kind: 'error', message: 'Failed to delete document' });
    }
  };

  // Start parsing
  const handleStartParsing = async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/ragflow/domains/${workspaceId}/parse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        addToast({ kind: 'success', message: 'Parsing started! Progress will update automatically.' });
        fetchData();
      } else {
        const error = await res.json();
        addToast({ kind: 'error', message: error.detail || 'Failed to start parsing' });
      }
    } catch (e) {
      addToast({ kind: 'error', message: 'Failed to start parsing' });
    }
  };

  const handleSyncStats = async () => {
    const ok = await syncWorkspaceStats(false);
    if (ok) {
      addToast({ kind: 'success', message: 'Workspace stats synced' });
      await fetchData();
    }
  };

  const handleRetryIngestion = async () => {
    if (!workspaceId) return;
    setIsRetrying(true);
    try {
      const res = await fetch(`${API_BASE}/v1/ragflow/domains/${workspaceId}/retry-ingestion`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const result = await res.json();
        addToast({
          kind: 'success',
          message: result.documents_queued > 0
            ? `Reprocessing ${result.documents_queued} document(s)...`
            : 'No documents to retry.',
        });
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        addToast({ kind: 'error', message: err.detail || 'Failed to retry ingestion' });
      }
    } catch (e) {
      addToast({ kind: 'error', message: 'Failed to retry ingestion' });
    } finally {
      setIsRetrying(false);
    }
  };

  // Check if chat is available
  const canChat =
    supportsChatWithoutKnowledgeBase ||
    knowledgeBases.some((kb) => kb.chunk_count > 0) ||
    ((workspace?.chunk_count || 0) > 0);
  const hasPendingDocs = documents.some(d => d.status === 'pending');
  const hasParsingDocs = documents.some(d => d.status === 'parsing');
  const hasFailedDocs = documents.some(d => d.status === 'failed');
  const hasRetryableDocs = hasFailedDocs || hasParsingDocs || hasPendingDocs;
  const showSyncBanner = hasParsingDocs || selectedKnowledgeBaseSyncing;

  const workspaceDescription = String(workspace?.description || '').trim();
  const workspaceSummary =
    workspaceDescription ||
    (isAgentMeshWorkspace
      ? 'Configure how Agent Mesh retrieves from connected sources and how this workspace handles knowledge, ingestion, and chat.'
      : workspaceCapabilities.supportsAgentCoreRuntimeSettings
        ? "Manage this workspace's AgentCore runtime settings and documents synced to its Amazon Bedrock Knowledge Base."
      : 'Manage this workspace’s knowledge bases, ingestion pipeline, and chat defaults.');
  const totalKnowledgeBaseDocuments = knowledgeBases.reduce((sum, kb) => sum + (kb.document_count || 0), 0);
  const totalKnowledgeBaseChunks = knowledgeBases.reduce((sum, kb) => sum + (kb.chunk_count || 0), 0);
  const unstructuredDocumentCount = Math.max(workspace?.document_count ?? 0, totalKnowledgeBaseDocuments, documents.length);
  const unstructuredChunkCount = Math.max(workspace?.chunk_count ?? 0, totalKnowledgeBaseChunks);
  const agentCoreKnowledgeCount = Math.max(acUploadedFiles.length, workspace?.document_count ?? 0);

  const renderWorkspaceChatSettingsSurface = () => {
    const plannerMode = getAgentMeshPlannerMode(workspace?.workspace_config);
    const showPlannerSettings = workspaceCapabilities.supportsAgentMeshPlanner;
    const showModelSettings = workspaceCapabilities.supportsRagPromptTemplateSettings;

    return (
      <div className="space-y-6">
        <header className="flex flex-col gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
            Workspace / Settings
          </p>
          <h1 className="font-title text-h1 leading-tight text-charcoal dark:text-gray-100">
            Workspace settings
          </h1>
          <p className="max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
            Configure workspace-level chat routing and model defaults.
          </p>
        </header>

        {showPlannerSettings && (
          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
            <div className="border-b border-gray-200 px-5 py-4 dark:border-dark-border">
              <h2 className="flex items-center gap-2 text-base font-semibold text-charcoal dark:text-gray-100">
                <Cog6ToothIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                Planner routing
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
                Auto uses direct paths for single RAG or structured sources, and planner routing for connector-only or mixed-source workspaces.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 px-5 py-5">
              {(['auto', 'on', 'off'] as PlannerMode[]).map((mode) => (
                <Button
                  key={mode}
                  type="button"
                  size="sm"
                  variant={plannerMode === mode ? 'brand' : 'outline'}
                  onClick={() => handlePlannerModeChange(mode)}
                  disabled={savingPlannerMode !== null}
                >
                  {savingPlannerMode === mode && <Spinner size="xs" className="text-current" />}
                  {mode === 'auto' ? 'Auto' : mode === 'on' ? 'On' : 'Off'}
                </Button>
              ))}
            </div>
          </section>
        )}

        {showModelSettings && (
          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
            <div className="border-b border-gray-200 px-5 py-4 dark:border-dark-border">
              <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">Chat model defaults</h2>
              <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
                Embedding stays tenant-wide. Chat model controls direct RAG and structured answer defaults. Agent Mesh planner paths use the tenant default model until workspace-level planner model selection is wired.
              </p>
            </div>
            <div className="grid gap-0 md:grid-cols-2">
              <div className="px-5 py-5 md:border-r md:border-gray-200 dark:md:border-dark-border">
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  Embedding
                </label>
                <p className="text-sm text-charcoal dark:text-white">
                  {ragSettings ? (
                    <>
                      <span className="font-medium capitalize">{ragSettings.embedding_provider}</span>
                      {' - '}
                      <span className="text-gray-600 dark:text-gray-300">{ragSettings.embedding_model}</span>
                    </>
                  ) : (
                    <span className="text-gray-400">Loading...</span>
                  )}
                </p>
              </div>
              <div className="px-5 py-5">
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  Chat model
                </label>
                <div className="flex items-center gap-2">
                  <Select
                    value={promptTemplate?.model ?? ''}
                    disabled={savingChatModel || ragChatModels.length === 0}
                    onValueChange={handleChatModelChange}
                    className="min-w-[260px]"
                  >
                    {ragChatModels.length === 0 ? (
                      <SelectOption value="" disabled>Loading...</SelectOption>
                    ) : (
                      <>
                        {promptTemplate?.model && !ragChatModels.some((m) => m.id === promptTemplate?.model) && (
                          <SelectOption value={promptTemplate.model}>{promptTemplate.model}</SelectOption>
                        )}
                        {ragChatModels.map((m) => (
                          <SelectOption key={m.id} value={m.id}>
                            {m.provider === 'bedrock' ? `Bedrock: ${m.name}` : `${m.name} (${m.provider})`}
                          </SelectOption>
                        ))}
                      </>
                    )}
                  </Select>
                  {savingChatModel && <Spinner size="sm" />}
                </div>
              </div>
            </div>
          </section>
        )}

        {!showPlannerSettings && !showModelSettings && (
          <section className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-10 text-center dark:border-dark-border dark:bg-dark-surface">
            <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">Workspace settings</h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
              No workspace-level chat settings are available for this workspace type yet.
            </p>
          </section>
        )}
      </div>
    );
  };

  const renderAgentCoreSettingsSurface = () => (
    <div className="space-y-6">
      <header className="flex flex-col gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
          Workspace / Settings
        </p>
        <h1 className="font-title text-h1 leading-tight text-charcoal dark:text-gray-100">
          Workspace settings
        </h1>
        <p className="max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
          Configure the AgentCore runtime defaults that apply when this workspace answers chat.
        </p>
      </header>

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
        <div className="border-b border-gray-200 px-5 py-4 dark:border-dark-border">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-charcoal dark:text-gray-100">
                <Cog6ToothIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                AgentCore runtime settings
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
                System prompt and model settings apply to the Bedrock AgentCore runtime for this workspace.
              </p>
            </div>
            {acConfig?.configured ? (
              <Badge variant="success">Runtime connected</Badge>
            ) : (
              <Badge variant="warning">Runtime not configured</Badge>
            )}
          </div>
          {acConfig?.configured && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Region: {acConfig.runtime_region} - Endpoint: {acConfig.endpoint_qualifier || 'DEFAULT'}
            </p>
          )}
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label htmlFor="agentcore-system-prompt" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
              System prompt
            </label>
            <textarea
              id="agentcore-system-prompt"
              value={acSystemPrompt}
              onChange={(e) => setAcSystemPrompt(e.target.value)}
              placeholder="Define what your AgentCore runtime should know and how it should behave..."
              rows={5}
              className="w-full resize-none rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-charcoal placeholder:text-gray-400 dark:border-dark-border dark:bg-dark-surface-2 dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
              Model
            </label>
            <Select
              value={acModelId}
              onValueChange={setAcModelId}
              className="w-full"
            >
              <SelectOption value="">Runtime default</SelectOption>
              <SelectOption value="us.anthropic.claude-sonnet-4-20250514-v1:0">Claude Sonnet 4</SelectOption>
              <SelectOption value="us.anthropic.claude-3-7-sonnet-20250219-v1:0">Claude 3.7 Sonnet</SelectOption>
              <SelectOption value="us.anthropic.claude-3-5-haiku-20241022-v1:0">Claude 3.5 Haiku</SelectOption>
              <SelectOption value="us.amazon.nova-pro-v1:0">Amazon Nova Pro</SelectOption>
              <SelectOption value="us.amazon.nova-lite-v1:0">Amazon Nova Lite</SelectOption>
            </Select>
          </div>
          <div className="flex justify-end">
            <Button type="button" onClick={handleSaveAgentCoreSettings} disabled={acSaving}>
              {acSaving ? <Spinner size="xs" /> : <Cog6ToothIcon className="h-4 w-4" />}
              Save settings
            </Button>
          </div>
        </div>
      </section>
    </div>
  );

  const renderAgentCoreKnowledgeSurface = () => (
    <div className="space-y-6">
      <section>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
              AgentCore knowledge
            </p>
            <h2 className="mt-1 font-title text-[28px] leading-tight text-charcoal dark:text-gray-100">
              Documents synced to Bedrock
            </h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
              Uploads here go to this workspace&apos;s AgentCore document endpoint and sync into the configured Amazon Bedrock Knowledge Base. They do not create native Eliza RAG knowledge bases.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={agentCoreFileInputRef}
              type="file"
              multiple
              onChange={(e) => {
                void handleAgentCoreUpload(e.target.files);
                e.target.value = '';
              }}
              className="hidden"
              accept=".pdf,.txt,.md,.doc,.docx,.csv,.html"
            />
            <Button
              type="button"
              size="sm"
              variant="brand"
              onClick={() => agentCoreFileInputRef.current?.click()}
              disabled={acUploading}
            >
              {acUploading ? <Spinner size="xs" className="text-current" /> : <CloudArrowUpIcon className="h-4 w-4" />}
              Upload AgentCore documents
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={handleAgentCoreSync} disabled={acSyncing}>
              {acSyncing ? <Spinner size="xs" /> : <ArrowPathIcon className="h-4 w-4" />}
              Sync Bedrock knowledge
            </Button>
            <Button
              type="button"
              size="sm"
              variant="link"
              onClick={() => workspaceId && navigate(`/workspaces/${workspaceId}/settings`)}
            >
              Configure system prompt
            </Button>
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-[20px] border border-gray-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)] dark:border-dark-border dark:bg-dark-surface">
          <div className="grid gap-5 border-b border-gray-200 px-5 py-4 dark:border-dark-border md:grid-cols-4">
            <SummaryStat label="Destination" value="AgentCore" detail="documents" />
            <SummaryStat label="Index" value="Bedrock" detail="Knowledge Base" />
            <SummaryStat label="Uploaded" value={String(agentCoreKnowledgeCount)} detail="files" />
            <SummaryStat label="Runtime" value={acConfig?.configured ? 'Connected' : 'Not configured'} detail={acConfig?.runtime_region || ''} />
          </div>

          <div className="px-5 py-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-eliza-red/10 text-eliza-red">
                <BookOpenIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                  Active knowledge path
                </p>
                <h3 className="mt-1 text-base font-semibold text-charcoal dark:text-gray-100">
                  AgentCore document upload - Amazon Bedrock Knowledge Base
                </h3>
                <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
                  Use this single upload area for AgentCore workspaces. Native RAG upload and knowledge-base creation are hidden because this workspace answers through AgentCore.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-[20px] border border-gray-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)] dark:border-dark-border dark:bg-dark-surface">
        <div className="border-b border-gray-200 px-5 py-4 dark:border-dark-border">
          <h3 className="text-sm font-semibold text-charcoal dark:text-white">Recently uploaded AgentCore documents</h3>
          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
            Files listed here were uploaded in this browser session. Bedrock indexing state is controlled by the AgentCore sync.
          </p>
        </div>
        {acUploadedFiles.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-dark-border">
            {acUploadedFiles.map((filename, index) => (
              <div key={`${filename}-${index}`} className="flex items-center gap-3 px-5 py-3 text-sm">
                <CheckCircleIcon className="h-4 w-4 text-emerald-600" />
                <span className="min-w-0 flex-1 truncate text-charcoal dark:text-gray-100">{filename}</span>
                <Badge variant="secondary">uploaded</Badge>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <DocumentTextIcon className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
            <h3 className="text-lg font-semibold text-charcoal dark:text-white">No AgentCore documents uploaded here yet</h3>
            <p className="mt-2 max-w-xl text-sm leading-6 text-gray-500 dark:text-gray-400">
              Upload files here when they should be available to the AgentCore runtime through Bedrock knowledge retrieval.
            </p>
            <Button type="button" className="mt-5" onClick={() => agentCoreFileInputRef.current?.click()}>
              <CloudArrowUpIcon className="h-4 w-4" />
              Choose AgentCore files
            </Button>
          </div>
        )}
      </section>
    </div>
  );

  const renderUnstructuredKnowledgeSurface = () => {
    const selectedStatus = selectedKnowledgeBase?.status || (unstructuredDocumentCount > 0 ? 'active' : 'not configured');
    const emptyMessage = selectedKnowledgeBaseId
      ? 'Upload documents to build this knowledge base.'
      : supportsChatWithoutKnowledgeBase
        ? 'This workspace can chat without a knowledge base. Create one when uploaded workspace documents should participate in retrieval.'
        : 'Create a knowledge base first, then upload documents.';
    const selectedSourceLabel = sourceTypeLabel(selectedKnowledgeBase?.source_type);
    const selectedParserLabel = selectedKnowledgeBase?.parser_type || 'default';
    const indexedKnowledgeBaseCount = knowledgeBases.filter((kb) => (kb.chunk_count || 0) > 0).length;
    const s3KnowledgeBaseCount = knowledgeBases.filter((kb) => kb.source_type === 's3').length;
    const platformKnowledgeBaseCount = knowledgeBases.length - s3KnowledgeBaseCount;
    const describeCreatedAt = (createdAt: string) => {
      const timestamp = new Date(createdAt).getTime();
      if (Number.isNaN(timestamp)) return '';
      return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(timestamp);
    };
    const fileKind = (doc: RAGFlowDocument) => {
      const extension = doc.original_filename.split('.').pop()?.toUpperCase();
      return extension && extension.length <= 5 ? extension : 'FILE';
    };

    return (
      <div className="space-y-8">
        <section>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                Knowledge bases
              </p>
              <h2 className="mt-1 font-title text-[32px] leading-tight text-charcoal dark:text-gray-100">
                Attached knowledge bases
              </h2>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                {workspaceCapabilities.supportsOntologyAuthoring
                  ? 'Each knowledge base holds the files this workspace retrieves from. Select a base to review files, manage uploads, and keep retrieval aligned with the unified ontology.'
                  : 'Each knowledge base holds the files this workspace retrieves from. Select a base to review files, manage uploads, and keep retrieval ready.'}
              </p>
              <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                <span><strong className="text-charcoal dark:text-gray-100">{knowledgeBases.length}</strong> knowledge bases</span>
                <span aria-hidden="true">.</span>
                <span><strong className="text-charcoal dark:text-gray-100">{unstructuredDocumentCount}</strong> files</span>
                <span aria-hidden="true">.</span>
                <span><strong className="text-charcoal dark:text-gray-100">{unstructuredChunkCount}</strong> indexed chunks</span>
                <span aria-hidden="true">.</span>
                <span>{s3KnowledgeBaseCount} S3 sync</span>
                <span aria-hidden="true">.</span>
                <span>{platformKnowledgeBaseCount} platform upload</span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowCreateKbModal(true)}>
                <PlusIcon className="h-4 w-4" />
                New knowledge base
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.md,.xlsx,.pptx,.html"
              />
            </div>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
          <aside className="space-y-3">
            {knowledgeBases.length > 0 ? (
              knowledgeBases.map((kb) => {
                const selected = selectedKnowledgeBaseId === kb.id;
                const kbSourceLabel = sourceTypeLabel(kb.source_type);
                return (
                  <div
                    key={kb.id}
                    className={`rounded-[18px] border bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.05)] transition-colors dark:bg-dark-surface ${
                      selected
                        ? 'border-eliza-red/50 ring-2 ring-eliza-red/10 dark:border-eliza-red/60'
                        : 'border-gray-200 hover:border-gray-300 dark:border-dark-border dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          selectKnowledgeBaseId(kb.id);
                          void fetchData(kb.id);
                        }}
                        className="flex min-w-0 flex-1 items-start gap-3 text-left"
                      >
                        <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
                          kb.source_type === 's3'
                            ? 'bg-orange-50 text-orange-600 dark:bg-orange-950/30 dark:text-orange-300'
                            : 'bg-eliza-red/10 text-eliza-red'
                        }`}>
                          <DocumentTextIcon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-charcoal dark:text-white">
                            {kb.name}
                          </span>
                          <span className="mt-2 block text-xs font-semibold text-eliza-red">
                            {kbSourceLabel}
                            <span className="font-normal text-gray-400 dark:text-gray-500"> - {kb.document_count} files - {kb.chunk_count} chunks</span>
                          </span>
                        </span>
                      </button>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`h-2.5 w-2.5 rounded-full ${
                          knowledgeBaseStatusVariant(kb.status) === 'success'
                            ? 'bg-emerald-500'
                            : knowledgeBaseStatusVariant(kb.status) === 'warning'
                              ? 'bg-amber-400'
                              : knowledgeBaseStatusVariant(kb.status) === 'danger'
                                ? 'bg-red-500'
                                : 'bg-gray-300'
                        }`} />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteKnowledgeBase(kb)}
                          disabled={deletingKnowledgeBaseId !== null}
                          className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
                          aria-label={`Delete ${kb.name}`}
                        >
                          {deletingKnowledgeBaseId === kb.id ? (
                            <Spinner size="xs" />
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    {kb.description && (
                      <p className="mt-3 line-clamp-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
                        {kb.description}
                      </p>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="rounded-[18px] border border-dashed border-gray-300 bg-white p-5 text-sm text-gray-500 dark:border-dark-border dark:bg-dark-surface dark:text-gray-400">
                Create a knowledge base to attach files to this workspace.
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              className="h-12 w-full justify-center border-dashed"
              onClick={() => setShowCreateKbModal(true)}
            >
              <PlusIcon className="h-4 w-4" />
              New knowledge base
            </Button>
          </aside>

          <div className="overflow-hidden rounded-[20px] border border-gray-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)] dark:border-dark-border dark:bg-dark-surface">
            <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-5 dark:border-dark-border lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-start gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-eliza-red/10 text-eliza-red">
                  <DocumentTextIcon className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate font-title text-[24px] leading-tight text-charcoal dark:text-gray-100">
                    {selectedKnowledgeBase?.name || 'No knowledge base selected'}
                  </h3>
                  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                    {selectedKnowledgeBase ? (
                      <>
                        <span className="font-semibold text-eliza-red">{selectedSourceLabel}</span>
                        <span aria-hidden="true">.</span>
                        <span>{selectedParserLabel} parser</span>
                        <span aria-hidden="true">.</span>
                        <span>{selectedStatus}</span>
                      </>
                    ) : (
                      <span>Select a knowledge base to review and manage its files.</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="brand"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || !selectedKnowledgeBaseId}
                >
                  {isUploading ? <Spinner size="xs" className="text-current" /> : <CloudArrowUpIcon className="h-4 w-4" />}
                  Upload files
                </Button>
                {hasPendingDocs && (
                  <Button variant="outline" size="sm" onClick={handleStartParsing}>
                    <PlayIcon className="h-4 w-4" />
                    Parse pending
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryIngestion}
                  disabled={isRetrying || !hasRetryableDocs}
                >
                  {isRetrying ? <Spinner size="xs" /> : <ArrowPathIcon className="h-4 w-4" />}
                  Re-index
                </Button>
                {selectedKnowledgeBase && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteKnowledgeBase(selectedKnowledgeBase)}
                    disabled={deletingKnowledgeBaseId !== null}
                    className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
                    aria-label={`Delete ${selectedKnowledgeBase.name}`}
                  >
                    {deletingKnowledgeBaseId === selectedKnowledgeBase.id ? (
                      <Spinner size="xs" />
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            {showSyncBanner && (
              <div className="border-b border-blue-100 bg-blue-50 px-5 py-3 dark:border-blue-800 dark:bg-blue-900/20">
                <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  {hasParsingDocs
                    ? 'Documents are being parsed. This page auto-refreshes.'
                    : 'Knowledge base sync is running. Documents will appear here shortly.'}
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <div className="min-w-[780px]">
                <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3 dark:border-dark-border dark:bg-dark-surface-2">
                  <div className="grid flex-1 grid-cols-[36px_minmax(0,1fr)_130px_90px_150px_42px] items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                    <span />
                    <span>File</span>
                    <span>Status</span>
                    <span>Chunks</span>
                    <span>Uploaded</span>
                    <span />
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={handleSyncStats} disabled={isSyncingStats}>
                    {isSyncingStats ? <Spinner size="xs" /> : <ArrowPathIcon className="h-4 w-4" />}
                    Refresh
                  </Button>
                </div>

                {documents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                    <DocumentTextIcon className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
                    <h3 className="mb-2 text-lg font-semibold text-charcoal dark:text-white">
                      No documents yet
                    </h3>
                    <p className="mb-5 max-w-xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                      {emptyMessage}
                    </p>
                    {!selectedKnowledgeBaseId ? (
                      <Button onClick={() => setShowCreateKbModal(true)}>
                        <PlusIcon className="h-4 w-4" />
                        Create Knowledge Base
                      </Button>
                    ) : (
                      <Button onClick={() => fileInputRef.current?.click()}>
                        <CloudArrowUpIcon className="h-4 w-4" />
                        Upload Documents
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-dark-border">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="grid grid-cols-[36px_minmax(0,1fr)_130px_90px_150px_42px] items-center gap-3 px-5 py-4"
                      >
                        <span className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 dark:border-dark-border" />
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-eliza-red/10 text-xs font-semibold text-eliza-red">
                            {fileKind(doc)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-charcoal dark:text-white">
                              {doc.original_filename}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatFileSize(doc.file_size)}
                              {doc.token_count ? ` - ${doc.token_count} tokens` : ''}
                            </p>
                            {doc.processing_error && doc.status === 'failed' && (
                              <p className="truncate text-xs text-red-500" title={doc.processing_error}>
                                {doc.processing_error}
                              </p>
                            )}
                          </div>
                        </div>
                        <StatusBadge status={doc.status} progress={doc.progress} />
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {doc.chunk_count || '-'}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {describeCreatedAt(doc.created_at) || '-'}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
                          aria-label={`Delete ${doc.original_filename}`}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-5 py-3 text-sm text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <span>
                {documents.length} files - {indexedKnowledgeBaseCount} indexed bases - {selectedKnowledgeBase?.chunk_count || 0} chunks in selected base
              </span>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={!selectedKnowledgeBaseId}
              >
                Upload more
                <PlusIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderUnsupportedConnectorAccessSurface = () => (
    <section className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-10 text-center dark:border-dark-border dark:bg-dark-surface">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
        <LinkIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
      </div>
      <h2 className="mt-4 text-base font-semibold text-charcoal dark:text-gray-100">
        Connector access is not available for this workspace type
      </h2>
      <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
        Agent Mesh connector source access is only shown for Agent Mesh workspaces. Use the knowledge or runtime settings that match this workspace instead.
      </p>
    </section>
  );

  const renderUnsupportedKnowledgeSurface = () => (
    <section className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-10 text-center dark:border-dark-border dark:bg-dark-surface">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
        <DocumentTextIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
      </div>
      <h2 className="mt-4 text-base font-semibold text-charcoal dark:text-gray-100">
        Knowledge bases are not available for this workspace type
      </h2>
      <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
        This workspace uses a specialized retrieval backend, so uploaded evidence is managed outside the native knowledge base surface.
      </p>
    </section>
  );

  const renderKnowledgeSurface = () => {
    if (workspaceCapabilities.supportsAgentCoreDocumentUpload) {
      return renderAgentCoreKnowledgeSurface();
    }
    if (workspaceCapabilities.supportsNativeRagKnowledgeBases) {
      return renderUnstructuredKnowledgeSurface();
    }
    return renderUnsupportedKnowledgeSurface();
  };

  const renderConnectorAccessSurface = () =>
    workspaceCapabilities.supportsAgentMeshConnectorAccess ? (
      <WorkspaceConnectorAccessPanel
        workspaceConfig={workspace?.workspace_config}
        onSaveWorkspaceConfig={handleSaveWorkspaceConfig}
        onOpenConnectionManager={handleOpenConnectionManager}
        canManageConnections={canManageConnections}
      />
    ) : (
      renderUnsupportedConnectorAccessSurface()
    );

  const semanticSurfaceSupported = (surface: SemanticShellSurface) => {
    if (surface === 'overview') return true;
    if (surface === 'setup') return workspaceCapabilities.supportsStructuredWarehouseScope;
    if (surface === 'ontology') return workspaceCapabilities.supportsOntologyAuthoring;
    if (surface === 'verified_queries') return workspaceCapabilities.supportsVerifiedQueries;
    if (surface === 'context_packs') return workspaceCapabilities.supportsContextPacks;
    if (surface === 'corrections') return workspaceCapabilities.supportsOntologyAuthoring;
    if (surface === 'mcp') return workspaceCapabilities.supportsMcpExposure;
    return true;
  };

  const renderUnsupportedSemanticSurface = (surface: SemanticShellSurface) => (
    <section className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-10 text-center dark:border-dark-border dark:bg-dark-surface">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
        <BookOpenIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
      </div>
      <h2 className="mt-4 text-base font-semibold text-charcoal dark:text-gray-100">
        {SEMANTIC_SURFACE_LABEL[surface]} is not available for this workspace type
      </h2>
      <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
        This semantic context surface is only shown for workspaces with matching runtime support. Use the overview or knowledge surfaces that match this workspace instead.
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-5"
        onClick={() => navigate(`/workspaces/${workspaceId}`)}
      >
        Back to overview
      </Button>
    </section>
  );

  const renderSemanticContextShell = (surface: SemanticShellSurface) => {
    if (!workspace || !workspaceId) return null;

    if (!semanticSurfaceSupported(surface)) {
      return renderUnsupportedSemanticSurface(surface);
    }

    return (
      <div className="space-y-6">
        {shouldLoadSemanticContext && semanticContextQuery.isLoading && (
          <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600 dark:border-dark-border dark:bg-dark-surface dark:text-gray-300">
            <Spinner size="sm" />
            Loading backend semantic context...
          </div>
        )}
        {shouldLoadSemanticContext && semanticContextQuery.isError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200">
            Failed to load backend semantic context. Workspace setup state is not shown from fallback config.
          </div>
        )}
        {(semanticContextQuery.data || !shouldLoadSemanticContext) && (
          <WorkspaceSemanticContextShell
            workspaceId={Number(workspaceId)}
            workspaceName={workspaceDisplayName}
            workspaceDescription={workspaceSummary}
            draft={structuredAnalyticsDraft}
            semanticContext={semanticContextQuery.data}
            workspaceConfig={workspace.workspace_config}
            canChat={canChat}
            unstructuredCount={
              workspaceCapabilities.supportsAgentCoreDocumentUpload
                ? agentCoreKnowledgeCount
                : knowledgeBases.length
            }
            unstructuredDocumentCount={
              workspaceCapabilities.supportsAgentCoreDocumentUpload
                ? agentCoreKnowledgeCount
                : unstructuredDocumentCount
            }
            unstructuredChunkCount={
              workspaceCapabilities.supportsAgentCoreDocumentUpload ? 0 : unstructuredChunkCount
            }
            unstructuredContent={
              renderKnowledgeSurface()
            }
            connectorsContent={
              workspaceCapabilities.supportsAgentMeshConnectorAccess
                ? renderConnectorAccessSurface()
                : undefined
            }
            overviewEyebrow={workspaceCapabilities.overviewEyebrow}
            showConnectorsSurface={workspaceCapabilities.supportsAgentMeshConnectorAccess}
            showStructuredSurface={workspaceCapabilities.supportsStructuredWarehouseScope}
            showOntologySurface={workspaceCapabilities.supportsOntologyAuthoring}
            showContextPacksSurface={workspaceCapabilities.supportsContextPacks}
            showVerifiedQueriesSurface={workspaceCapabilities.supportsVerifiedQueries}
            showCorrectionsSurface={workspaceCapabilities.supportsOntologyAuthoring}
            showMcpSurface={workspaceCapabilities.supportsMcpExposure}
            onOpenChat={workspace ? () => navigate(`/chat/${workspace.id}`) : undefined}
            onOpenWarehouseConnections={() => navigateDataConnectionsForStructured({ category: 'warehouse' })}
            onOpenCatalogConnections={() => navigateDataConnectionsForStructured({ category: 'catalog' })}
            onOpenCatalogApplet={() => navigate('/catalog')}
            onOpenWarehouseRegistry={() =>
              navigateDataConnectionsForStructured({ category: 'warehouse', sourceType: 'bigquery' })
            }
            onSaveStructuredScope={async (tableIds, warehouseConnectorId) => {
              await updateSemanticScope.mutateAsync({
                warehouse_connector_id:
                  warehouseConnectorId ??
                  semanticContextQuery.data?.structured_scope.warehouse_connector?.id ??
                  null,
                table_ids: tableIds,
              });
            }}
            onSaveOntologyDraft={(payload) => saveSemanticOntologyDraft.mutateAsync(payload)}
            onCompileRuntimePack={() => compileSemanticRuntimePack.mutateAsync()}
            onPublishRuntimePack={(packId) => publishSemanticRuntimePack.mutateAsync(packId)}
            ontologyGenerationSettings={ontologyGenerationSettingsQuery.data}
            ontologySuggestions={ontologyGenerationSuggestionsQuery.data ?? []}
            ontologyGenerationStream={ontologyGenerationRunStream}
            isOntologySuggestionsLoading={ontologyGenerationSuggestionsQuery.isLoading}
            isDraftingOntologySuggestions={createOntologyGenerationRun.isPending || ontologyGenerationRunStream.isStreaming}
            onDraftOntologyWithAI={async () => {
              const run = await createOntologyGenerationRun.mutateAsync();
              setActiveOntologyGenerationRunId(run.id);
              return run;
            }}
            onApplyOntologySuggestion={(suggestionId) => applyOntologySuggestion.mutateAsync(suggestionId)}
            onApplyOntologySuggestionsBatch={(runId, suggestionIds) =>
              applyOntologySuggestionsBatch.mutateAsync({ runId, suggestionIds })
            }
            onRejectOntologySuggestion={(suggestionId) => rejectOntologySuggestion.mutateAsync(suggestionId)}
            verifiedQueries={verifiedQueriesQuery.data?.verified_queries ?? []}
            isVerifiedQueriesLoading={verifiedQueriesQuery.isLoading}
            isDraftingVerifiedQuery={draftVerifiedQueryWithAI.isPending}
            isSavingVerifiedQuery={createVerifiedQuery.isPending || updateVerifiedQuery.isPending}
            isReviewingVerifiedQuery={reviewVerifiedQuery.isPending}
            isDeletingVerifiedQuery={deleteVerifiedQuery.isPending}
            onCreateVerifiedQuery={(payload) => createVerifiedQuery.mutateAsync(payload)}
            onDraftVerifiedQueryWithAI={(payload) => draftVerifiedQueryWithAI.mutateAsync(payload)}
            onUpdateVerifiedQuery={(queryId, payload) => updateVerifiedQuery.mutateAsync({ queryId, data: payload })}
            onApproveVerifiedQuery={(queryId) => reviewVerifiedQuery.mutateAsync({ queryId, action: 'approve' })}
            onRejectVerifiedQuery={(queryId) => reviewVerifiedQuery.mutateAsync({ queryId, action: 'reject' })}
            onDeleteVerifiedQuery={(queryId) => deleteVerifiedQuery.mutateAsync(queryId)}
            onNotify={addToast}
            mcpPolicy={mcpPolicyQuery.data}
            mcpReadiness={mcpReadinessQuery.data}
            isMcpPolicyLoading={mcpPolicyQuery.isLoading}
            isMcpReadinessLoading={mcpReadinessQuery.isLoading}
            mcpEnabled={workspace.mcp_enabled}
            mcpAdminAllowed={workspace.mcp_admin_allowed}
            mcpToolSupport={workspaceCapabilities.mcpToolSupport}
            canManageMcpPolicy={hasAnyPermission(['admin:settings:write', 'platform:admin'])}
            canUpdateWorkspace={hasAnyPermission(['workspaces:write', 'platform:admin'])}
            onToggleMcp={handleToggleMcp}
            isUpdatingMcp={isUpdatingMcp}
            activeSurface={surface}
            onSurfaceChange={setActiveSemanticSurface}
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Layout breadcrumbs={workspaceBreadcrumbs}>
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <Spinner size="lg" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading workspace...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={workspaceBreadcrumbs}>
      <div className="flex-1 flex flex-col h-full bg-gray-50 dark:bg-dark-bg overflow-hidden">
        {/* Content area */}
        <div className="flex-1 overflow-y-auto">
          <div className={isSemanticOntologySurface ? 'h-full w-full' : 'mx-auto w-full max-w-[1480px] p-6'}>
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              defaultValue="overview"
              className={isSemanticOntologySurface ? 'h-full' : 'space-y-6'}
            >
              <TabsContent value="overview" className="m-0 space-y-6">
                {renderSemanticContextShell('overview')}
              </TabsContent>

              <TabsContent value="semantic" className={isSemanticOntologySurface ? 'm-0 h-full' : 'm-0 space-y-6'}>
                {renderSemanticContextShell(activeSemanticSurface)}
              </TabsContent>

              <TabsContent value="connectors" className="m-0 space-y-6">
                {workspace && (
                  <div id="workspace-connector-access-panel">
                    {renderConnectorAccessSurface()}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="members" className="m-0 space-y-6">
                <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-10 text-center dark:border-dark-border dark:bg-dark-surface">
                  <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">Members & roles</h2>
                  <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                    Workspace membership controls are reserved for the workspace settings read model. No backend state is invented in this surface.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="settings" className="m-0 space-y-6">
                {workspaceCapabilities.supportsAgentCoreRuntimeSettings ? (
                  renderAgentCoreSettingsSurface()
                ) : (
                  renderWorkspaceChatSettingsSurface()
                )}
              </TabsContent>


              <TabsContent value="knowledge" className="m-0 space-y-6">
                {renderKnowledgeSurface()}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {showCreateKbModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreateKbModal(false)} />
          <div className="relative w-full max-w-xl rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-dark-border dark:bg-dark-surface">
            <h2 className="text-lg font-semibold text-charcoal dark:text-white">Create Knowledge Base</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Configure source, parser, and defaults for this workspace knowledge base.
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-charcoal dark:text-white">Name</label>
                <Input value={kbName} onChange={(e) => setKbName(e.target.value)} placeholder="Policy Docs" />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-charcoal dark:text-white">Description</label>
                <Input
                  value={kbDescription}
                  onChange={(e) => setKbDescription(e.target.value)}
                  placeholder="Optional description"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-charcoal dark:text-white">Source</label>
                  <Select
                    value={kbSourceType}
                    onValueChange={(value) => setKbSourceType(value as 'elasticsearch' | 's3' | 'local_directory')}
                    className="w-full"
                  >
                    <SelectOption value="elasticsearch">Platform upload</SelectOption>
                    <SelectOption value="s3">S3 sync</SelectOption>
                    <SelectOption value="local_directory">Local Directory (virtual path)</SelectOption>
                  </Select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-charcoal dark:text-white">Parser</label>
                  <Select
                    value={kbParserType}
                    onValueChange={(value) =>
                      setKbParserType(value as 'naive' | 'deepdoc' | 'gpt-4o' | 'docling' | 'custom-vlm')
                    }
                    className="w-full"
                  >
                    <SelectOption value="naive">naive</SelectOption>
                    <SelectOption value="deepdoc">deepdoc</SelectOption>
                    <SelectOption value="gpt-4o">gpt-4o</SelectOption>
                    <SelectOption value="docling">docling</SelectOption>
                    <SelectOption value="custom-vlm">custom-vlm</SelectOption>
                  </Select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-charcoal dark:text-white">
                  {kbSourceType === 'elasticsearch'
                    ? 'Upload Path (optional)'
                    : kbSourceType === 's3'
                      ? 'S3 Bucket Path (required)'
                      : 'Virtual Local Path (optional)'}
                </label>
                <Input
                  value={kbSourceValue}
                  onChange={(e) => setKbSourceValue(e.target.value)}
                  placeholder={
                    kbSourceType === 'elasticsearch'
                      ? 'Leave blank for direct uploads'
                      : kbSourceType === 's3'
                        ? 's3://tenant-documents/customers/acme/workspace-1/'
                        : '/imports/legal/'
                  }
                />
              </div>

              {kbParserType === 'custom-vlm' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-charcoal dark:text-white">
                    Custom VLM Model
                  </label>
                  <Input
                    value={kbCustomModel}
                    onChange={(e) => setKbCustomModel(e.target.value)}
                    placeholder="deepseek-ocr2@OpenAI-API-Compatible"
                  />
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowCreateKbModal(false)} disabled={isCreatingKb}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateKnowledgeBase}
                disabled={isCreatingKb || (kbSourceType === 's3' && !kbSourceValue.trim())}
              >
                {isCreatingKb ? <Spinner size="sm" className="mr-2" /> : <PlusIcon className="mr-2 h-4 w-4" />}
                Create Knowledge Base
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
